/**
 * Premium Upgrade Page
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Crown, Check, ChevronLeft, Gift, Zap, Star, 
  ShoppingCart, MapPin, Phone, Mail, User, Loader2, CreditCard
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuthStore } from '../../store';
import api from '../../services/api';
import { toast } from 'sonner';

// Premium API
const premiumAPI = {
  getInfo: () => api.get('/premium/info'),
  upgrade: (data) => api.post('/premium/upgrade', data),
  getSubscription: () => api.get('/premium/subscription'),
};

const PremiumUpgrade = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  
  const [premiumInfo, setPremiumInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    payer_name: '',
    payer_surname: '',
    payer_email: '',
    payer_phone: '',
    street_name: '',
    street_number: '',
    apartment: '',
    zip_code: '',
    city: '',
    state: '',
  });
  
  // Load premium info
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    const loadInfo = async () => {
      try {
        setLoading(true);
        const response = await premiumAPI.getInfo();
        setPremiumInfo(response.data);
        
        // Pre-fill form with user data
        if (user) {
          setFormData(prev => ({
            ...prev,
            payer_name: user.first_name || '',
            payer_surname: user.last_name || '',
            payer_email: user.email || '',
            payer_phone: user.phone || '',
          }));
        }
      } catch (err) {
        console.error('Error loading premium info:', err);
        setError('Error al cargar información');
      } finally {
        setLoading(false);
      }
    };
    
    loadInfo();
  }, [isAuthenticated, navigate, user]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleUpgrade = async (e) => {
    e.preventDefault();
    
    // Validate
    const required = ['payer_name', 'payer_surname', 'payer_email', 'payer_phone'];
    
    // If first time (free shelly), also require shipping address
    if (premiumInfo?.is_first_time) {
      required.push('street_name', 'street_number', 'zip_code', 'city', 'state');
    }
    
    const missing = required.filter(f => !formData[f]);
    if (missing.length > 0) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    
    try {
      setCheckoutLoading(true);
      setError(null);
      
      const response = await premiumAPI.upgrade({
        payer_name: formData.payer_name,
        payer_surname: formData.payer_surname,
        payer_email: formData.payer_email,
        payer_phone: formData.payer_phone,
        shipping_address: {
          street_name: formData.street_name,
          street_number: formData.street_number,
          apartment: formData.apartment,
          zip_code: formData.zip_code,
          city: formData.city,
          state: formData.state,
          country: 'México'
        }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Error al crear la orden');
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      toast.error(err.response?.data?.detail || 'Error al procesar');
    } finally {
      setCheckoutLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#7ED957]" />
      </div>
    );
  }
  
  // If already premium
  if (premiumInfo?.is_premium) {
    return (
      <div className="min-h-screen bg-gray-900">
        <header className="bg-gray-800/80 border-b border-gray-700 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-white">Premium</h1>
          </div>
        </header>
        
        <main className="max-w-2xl mx-auto p-4">
          <Card className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/50">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <Crown className="w-12 h-12 text-yellow-500" />
              </div>
              <CardTitle className="text-2xl text-white">¡Ya eres Premium!</CardTitle>
              <CardDescription className="text-gray-300">
                Tu suscripción está activa
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-400 mb-4">
                Tu suscripción expira el:{' '}
                <span className="text-white font-medium">
                  {new Date(premiumInfo.current_subscription?.expires_at).toLocaleDateString('es-MX', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </p>
              <Button onClick={() => navigate('/profile')} variant="outline">
                Volver a mi perfil
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }
  
  const product = premiumInfo?.product;
  
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800/80 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-white">Upgrade a Premium</h1>
          </div>
          {premiumInfo?.is_sandbox && (
            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
              Modo Sandbox
            </span>
          )}
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto p-4">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Benefits Card */}
          <div>
            <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30 mb-6">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                    <Crown className="w-7 h-7 text-yellow-500" />
                  </div>
                  <div>
                    <CardTitle className="text-white">{product?.title}</CardTitle>
                    <CardDescription className="text-gray-400">
                      {product?.duration_months} meses de beneficios
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-[#7ED957]" />
                    <span>Replays ilimitados</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-[#7ED957]" />
                    <span>Prioridad en generación de clips</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-[#7ED957]" />
                    <span>Descarga en alta calidad</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-[#7ED957]" />
                    <span>Sin marca de agua</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-[#7ED957]" />
                    <span>Soporte prioritario</span>
                  </div>
                </div>
                
                {/* Free Shelly Promo */}
                {premiumInfo?.is_first_time && (
                  <div className="mt-6 p-4 bg-[#7ED957]/10 border border-[#7ED957]/30 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Gift className="w-8 h-8 text-[#7ED957]" />
                      <div>
                        <p className="font-bold text-[#7ED957]">¡Promoción Primera Activación!</p>
                        <p className="text-sm text-gray-300">
                          Por tiempo limitado, incluye <span className="font-bold text-white">Botón Shelly GRATIS</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
              
              <CardFooter className="border-t border-yellow-500/20 pt-4">
                <div className="w-full">
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-400">Precio</span>
                    <div>
                      <span className="text-4xl font-bold text-white">${product?.price?.toLocaleString()}</span>
                      <span className="text-gray-400 ml-2">MXN</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 text-right">por {product?.duration_months} meses</p>
                </div>
              </CardFooter>
            </Card>
          </div>
          
          {/* Checkout Form */}
          <div>
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#7ED957]" />
                  Completar Upgrade
                </CardTitle>
              </CardHeader>
              
              <form onSubmit={handleUpgrade}>
                <CardContent className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                      {error}
                    </div>
                  )}
                  
                  {/* Personal info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-300">Nombre *</Label>
                      <Input
                        name="payer_name"
                        value={formData.payer_name}
                        onChange={handleInputChange}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="Juan"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Apellido *</Label>
                      <Input
                        name="payer_surname"
                        value={formData.payer_surname}
                        onChange={handleInputChange}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="García"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-300 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> Email *
                      </Label>
                      <Input
                        name="payer_email"
                        type="email"
                        value={formData.payer_email}
                        onChange={handleInputChange}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="email@ejemplo.com"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> Teléfono *
                      </Label>
                      <Input
                        name="payer_phone"
                        value={formData.payer_phone}
                        onChange={handleInputChange}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="5512345678"
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Shipping address - Required for first time (free Shelly) */}
                  {premiumInfo?.is_first_time && (
                    <div className="pt-4 border-t border-gray-700">
                      <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#7ED957]" />
                        Dirección de envío
                        <span className="text-xs text-[#7ED957] bg-[#7ED957]/10 px-2 py-0.5 rounded-full">
                          Para tu Shelly gratis
                        </span>
                      </h4>
                      
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          <div className="col-span-2">
                            <Label className="text-gray-300">Calle *</Label>
                            <Input
                              name="street_name"
                              value={formData.street_name}
                              onChange={handleInputChange}
                              className="bg-gray-700 border-gray-600 text-white"
                              placeholder="Av. Insurgentes"
                              required
                            />
                          </div>
                          <div>
                            <Label className="text-gray-300">Número *</Label>
                            <Input
                              name="street_number"
                              value={formData.street_number}
                              onChange={handleInputChange}
                              className="bg-gray-700 border-gray-600 text-white"
                              placeholder="123"
                              required
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-gray-300">Apartamento / Interior</Label>
                          <Input
                            name="apartment"
                            value={formData.apartment}
                            onChange={handleInputChange}
                            className="bg-gray-700 border-gray-600 text-white"
                            placeholder="Opcional"
                          />
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-gray-300">C.P. *</Label>
                            <Input
                              name="zip_code"
                              value={formData.zip_code}
                              onChange={handleInputChange}
                              className="bg-gray-700 border-gray-600 text-white"
                              placeholder="06600"
                              maxLength={5}
                              required
                            />
                          </div>
                          <div>
                            <Label className="text-gray-300">Ciudad *</Label>
                            <Input
                              name="city"
                              value={formData.city}
                              onChange={handleInputChange}
                              className="bg-gray-700 border-gray-600 text-white"
                              placeholder="CDMX"
                              required
                            />
                          </div>
                          <div>
                            <Label className="text-gray-300">Estado *</Label>
                            <Input
                              name="state"
                              value={formData.state}
                              onChange={handleInputChange}
                              className="bg-gray-700 border-gray-600 text-white"
                              placeholder="CDMX"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
                
                <CardFooter className="flex-col gap-4 border-t border-gray-700 pt-6">
                  <div className="w-full flex items-center justify-between text-lg">
                    <span className="text-gray-400">Total a pagar:</span>
                    <span className="text-2xl font-bold text-[#7ED957]">
                      ${product?.price?.toLocaleString()} MXN
                    </span>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={checkoutLoading}
                    className="w-full bg-[#009ee3] hover:bg-[#0088cc] text-white font-bold py-6 text-lg"
                  >
                    {checkoutLoading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Procesando...</>
                    ) : (
                      <><Crown className="w-5 h-5 mr-2" /> Activar Premium</>
                    )}
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    Serás redirigido a Mercado Pago para completar el pago de forma segura
                  </p>
                </CardFooter>
              </form>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PremiumUpgrade;
